<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper dq-platform">
    <div class="content">
      <div class="row align-items-center mb-4">
        <div class="col-sm-6">
          <h6 class="fw-medium d-inline-flex align-items-center mb-3 mb-sm-0">
            <router-link :to="backLink">
              <i class="ti ti-arrow-left me-2"></i>{{ backLabel }}
            </router-link>
            <span class="text-gray d-inline-flex ms-2">
              /
              <span v-if="loading"> Loading...</span>
              <span v-else>{{ companyName }}</span>
            </span>
          </h6>
        </div>

        <div class="col-sm-6">
          <div
            class="d-flex align-items-center justify-content-sm-end flex-wrap dq-page-actions"
          >
            <button
              v-if="!isTenantWorkspace"
              type="button"
              class="btn btn-primary d-inline-flex align-items-center me-2"
              @click="switchTab('notes')"
            >
              <i class="ti ti-circle-plus me-2"></i>Add Note
            </button>

            <button
              v-if="!isTenantWorkspace"
              type="button"
              class="btn btn-dark d-inline-flex align-items-center"
              @click="switchTab('email')"
            >
              <i class="ti ti-mail me-2"></i>Send Email
            </button>

            <div class="head-icons ms-2 mb-0">
              <a
                href="javascript:void(0);"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-original-title="Collapse"
                id="collapse-header"
                @click="toggleHeader"
              >
                <i class="ti ti-chevrons-up"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <CompanyDetailsSkeleton v-if="pageLoading" />

      <div v-else class="row">
        <div :class="isTenantWorkspace ? 'col-xl-6' : 'col-xl-4'" class="theiaStickySidebar">
          <div class="card card-bg-1">
            <div class="card-body p-0">
              <span
                class="avatar avatar-xl border bg-white rounded-circle m-auto d-flex mb-2"
              >
                <img
                  v-if="logoSrc"
                  :src="logoSrc"
                  class="w-auto h-auto"
                  alt="Company Logo"
                />
                <img
                  v-else
                  src="@/assets/img/company/company-11.svg"
                  class="w-auto h-auto"
                  alt="Company"
                />
              </span>

              <div class="text-center px-3 pb-3 border-bottom">
                <h5 class="d-flex align-items-center justify-content-center mb-1">
                  {{ companyName }}
                  <i
                    v-if="companyStatus === 'Active'"
                    class="ti ti-discount-check-filled text-success ms-1"
                  ></i>
                </h5>

                <p class="text-dark mb-0">
                  {{ safeText(companyAddress, "—") }}
                </p>

                <div v-if="activePlanName" class="mt-2">
                  <span class="badge bg-primary-transparent">
                    {{ activePlanName }}
                    <span v-if="activeSubStatus"> • {{ activeSubStatus }}</span>
                  </span>
                </div>

                <div
                  v-if="isSuperAdmin"
                  class="mt-3 d-flex justify-content-center gap-2 flex-wrap"
                >
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-success"
                    :disabled="updatingStatus"
                    @click="changeCompanyStatus('Active')"
                  >
                    Activate
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-warning"
                    :disabled="updatingStatus"
                    @click="changeCompanyStatus('Suspended')"
                  >
                    Suspend
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    :disabled="updatingStatus"
                    @click="changeCompanyStatus('Inactive')"
                  >
                    Deactivate
                  </button>
                </div>
              </div>

              <div class="p-3 border-bottom">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <h6>Basic Information</h6>
                </div>

                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="d-inline-flex align-items-center">
                    <i class="ti ti-phone me-2"></i>
                    Phone
                  </span>
                  <p class="text-dark mb-0">{{ safeText(companyPhone, "—") }}</p>
                </div>

                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="d-inline-flex align-items-center">
                    <i class="ti ti-mail-check me-2"></i>
                    Email
                  </span>
                  <span class="text-dark">{{ safeText(companyEmail, "—") }}</span>
                </div>

                <div class="d-flex align-items-center justify-content-between">
                  <span class="d-inline-flex align-items-center">
                    <i class="ti ti-gender-male me-2"></i>
                    Created On
                  </span>
                  <p class="text-dark mb-0">{{ formatDate(companyCreatedAt) }}</p>
                </div>
              </div>

              <div class="p-3 border-bottom">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <h6>Other Information</h6>
                </div>

                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="d-inline-flex align-items-center">
                    <i class="ti ti-mail-check me-2"></i>
                    Currency
                  </span>
                  <p class="text-dark mb-0">{{ safeText(companyCurrency, "—") }}</p>
                </div>

                <div class="d-flex align-items-center justify-content-between mb-2">
                  <span class="d-inline-flex align-items-center">
                    <i class="ti ti-globe me-2"></i>
                    Last Modified
                  </span>
                  <p class="text-dark mb-0">{{ formatDate(companyUpdatedAt) }}</p>
                </div>

                <div class="d-flex align-items-center justify-content-between">
                  <span class="d-inline-flex align-items-center">
                    <i class="ti ti-bookmark-plus me-2"></i>
                    Country
                  </span>
                  <p class="text-dark mb-0">
                    {{ safeText(companyCountry, "—") }}
                    <span v-if="companyCountryCode" class="text-muted">
                      ({{ companyCountryCode }})
                    </span>
                  </p>
                </div>
              </div>

              <div class="p-3 border-bottom">
                <h5 class="mb-3">Company Overview</h5>
                <div class="d-flex align-items-center flex-wrap gap-2">
                  <span
                    v-if="companyStatus"
                    :class="[
                      'badge',
                      companyStatus === 'Active'
                        ? 'badge-soft-success'
                        : companyStatus === 'Suspended'
                        ? 'badge-soft-warning'
                        : 'badge-soft-danger',
                    ]"
                  >
                    Status: {{ companyStatus }}
                  </span>

                  <span v-if="activePlanName" class="badge badge-soft-warning">
                    Plan: {{ activePlanName }}
                  </span>

                  <span v-if="billingPeriod" class="badge badge-soft-info">
                    Billing: {{ billingPeriodLabel }}
                  </span>

                  <span v-if="licenseExpiresAt" class="badge badge-soft-secondary">
                    Expires: {{ formatDate(licenseExpiresAt) }}
                  </span>

                  <span v-if="gracePeriodEndsAt" class="badge badge-soft-warning">
                    Grace Ends: {{ formatDate(gracePeriodEndsAt) }}
                  </span>

                  <span
                    v-if="!companyStatus && !activePlanName && !billingPeriod"
                    class="text-muted"
                  >
                    —
                  </span>
                </div>
              </div>

                <div v-if="isSuperAdmin" class="p-3 border-bottom">
                  <div class="d-flex align-items-center justify-content-between mb-3">
                    <h5 class="mb-0">Tenant Workspace</h5>
                    <span
                      v-if="workspaceInfo?.has_workspace"
                      class="badge badge-soft-success"
                    >
                      Provisioned
                    </span>
                    <span v-else class="badge badge-soft-warning">Not provisioned</span>
                  </div>

                  <div v-if="workspaceLoading" class="text-muted small">
                    Loading workspace details...
                  </div>

                  <template v-else>
                    <div v-if="workspaceInfo?.has_workspace" class="mb-3">
                      <div class="small text-muted mb-1">Subdomain</div>
                      <div class="fw-medium">{{ workspaceInfo?.api_domain || "—" }}</div>
                      <div class="small text-muted mt-2 mb-1">Workspace URL</div>
                      <a
                        v-if="workspaceInfo?.workspace_url"
                        :href="workspaceInfo.workspace_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-primary"
                      >
                        {{ workspaceInfo.workspace_url }}
                      </a>
                      <div class="small text-muted mt-2 mb-1">MySQL database</div>
                      <div class="d-flex align-items-center gap-2">
                        <code class="small text-break mb-0">
                          {{ workspaceInfo?.database_name || "—" }}
                        </code>
                        <button
                          v-if="workspaceInfo?.database_name"
                          type="button"
                          class="btn btn-sm btn-white"
                          title="Copy database name"
                          @click="copyWorkspaceDatabaseName"
                        >
                          <i class="ti ti-copy"></i>
                        </button>
                      </div>
                      <div class="small text-muted mt-2">
                        Status:
                        <span class="text-dark">
                          {{ workspaceInfo?.tenant?.provisioning_status || "—" }}
                        </span>
                      </div>
                    </div>

                    <div v-else class="mb-3">
                      <p class="small text-muted mb-3">
                        Migrating companies need a dedicated subdomain and tenant database
                        before users can log in. Assign a workspace URL, then import data
                        from Migration Center.
                      </p>
                      <label class="form-label">Workspace slug</label>
                      <div class="input-group mb-2">
                        <input
                          v-model="workspaceSlug"
                          type="text"
                          class="form-control"
                          placeholder="acme-corp"
                          @input="queueSlugCheck"
                        />
                        <span class="input-group-text">
                          .{{ tenantDomainLabel }}
                        </span>
                      </div>
                      <div v-if="slugPreviewUrl" class="small text-muted mb-2">
                        Preview: {{ slugPreviewUrl }}
                      </div>
                      <div
                        v-if="slugCheckMessage"
                        :class="[
                          'small mb-2',
                          slugAvailable ? 'text-success' : 'text-danger',
                        ]"
                      >
                        {{ slugCheckMessage }}
                      </div>
                      <button
                        type="button"
                        class="btn btn-primary w-100"
                        :disabled="migratingWorkspace || !workspaceSlug || !slugAvailable"
                        @click="provisionWorkspace"
                      >
                        <span
                          v-if="migratingWorkspace"
                          class="spinner-border spinner-border-sm me-2"
                        ></span>
                        Provision tenant workspace
                      </button>
                    </div>

                    <div class="d-flex flex-wrap gap-2">
                      <router-link
                        v-if="workspaceInfo?.has_workspace"
                        :to="migrationCenterLink"
                        class="btn btn-sm btn-outline-primary"
                      >
                        Open Migration Center
                      </router-link>
                      <button
                        v-if="workspaceInfo?.tenant?.id"
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        :disabled="runningTenantMigrate"
                        @click="runTenantSchemaMigrate"
                      >
                        Run tenant migrations
                      </button>
                    </div>
                  </template>
                </div>

                <div v-if="isSuperAdmin" class="p-3 border-bottom">
                  <div class="d-flex align-items-center justify-content-between mb-3">
                    <h5 class="mb-0">Plan Management</h5>
                    <span v-if="activePlanName" class="badge badge-soft-primary">
                      Current: {{ activePlanName }}
                  </span>
                </div>

                <div class="mb-3 small">
                  <div><strong>Status:</strong> {{ activeSubStatus || "none" }}</div>
                  <div class="text-muted">
                    Set Payment Status to <strong>Paid</strong> and click Change Plan to activate a license.
                    Public signups stay on trial; SuperAdmin / migrated companies should be paid + active.
                  </div>
                </div>

                <div
                  v-if="activeSubStatus === 'trialing' && activeSubscriptionId"
                  class="alert alert-warning py-2 px-3 mb-3"
                >
                  <div class="d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <span>This company is still on trial.</span>
                    <button
                      type="button"
                      class="btn btn-sm btn-success"
                      :disabled="managingLicense"
                      @click="activatePaidLicense"
                    >
                      Activate paid license
                    </button>
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label">Assign Plan</label>
                  <select class="form-select" v-model="planChangeForm.plan_id">
                    <option value="">Select plan</option>
                    <option
                      v-for="plan in activePlans"
                      :key="plan.id"
                      :value="String(plan.id)"
                    >
                      {{ plan.name }} ({{ plan.billing_period === "annual_license" ? "annual license" : plan.billing_period }}) - {{ plan.currency }}
                      {{ Number(plan.price || 0).toFixed(2) }}
                    </option>
                  </select>
                </div>

                <div class="mb-3">
                  <label class="form-label">Start Date</label>
                  <input
                    v-model="planChangeForm.starts_at"
                    type="date"
                    class="form-control"
                  />
                </div>

                <div class="mb-3">
                  <label class="form-label">Payment Status</label>
                  <select class="form-select" v-model="planChangeForm.payment_status">
                    <option value="unpaid">Unpaid</option>
                    <option value="paid">Paid</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </div>

                <div class="mb-3">
                  <label class="form-label">Payment Method</label>
                  <input
                    v-model="planChangeForm.payment_method"
                    type="text"
                    class="form-control"
                    placeholder="Manual, Mpesa, Bank Transfer"
                  />
                </div>

                <div class="small text-muted">
                  Changing the plan here expires the current active or trial subscription
                  and replaces it with the selected one.
                </div>

                  <button
                    type="button"
                    class="btn btn-primary w-100 mt-3"
                    :disabled="changingPlan || !planChangeForm.plan_id"
                    @click="changeCompanyPlan"
                  >
                    <span
                      v-if="changingPlan"
                      class="spinner-border spinner-border-sm me-2"
                    ></span>
                    Change Plan
                  </button>
                </div>

                <div v-if="isSuperAdmin" class="p-3 border-bottom">
                  <div class="d-flex align-items-center justify-content-between mb-2">
                    <h5 class="mb-0">Module Access Overrides</h5>
                    <span class="badge badge-soft-info">This company only</span>
                  </div>

                  <p class="small text-muted mb-3">
                    Manually enable or disable modules for this specific client company without changing the shared plan for other companies.
                  </p>

                  <div v-if="moduleOverrideLoading" class="border rounded p-3 text-muted">
                    Loading module access...
                  </div>
                  <div v-else-if="!moduleGroups.length" class="border rounded p-3 text-muted">
                    No module catalog is available for overrides.
                  </div>
                  <div v-else class="d-flex flex-column gap-3">
                    <div
                      v-for="group in moduleGroups"
                      :key="group.key"
                      class="border rounded p-3"
                    >
                      <div class="d-flex align-items-center justify-content-between mb-2">
                        <h6 class="mb-0">{{ group.label }}</h6>
                        <span class="small text-muted">
                          {{ groupEnabledCount(group) }} / {{ group.children?.length || 0 }} enabled
                        </span>
                      </div>

                      <div
                        v-for="child in group.children || []"
                        :key="child.key"
                        class="d-flex align-items-center justify-content-between gap-3 py-2 border-top"
                      >
                        <div class="flex-fill">
                          <div class="fw-medium text-dark">{{ child.label }}</div>
                          <div class="small text-muted d-flex align-items-center gap-2 flex-wrap">
                            <span
                              :class="[
                                'badge',
                                moduleInPlan(child.key) ? 'badge-soft-success' : 'badge-soft-secondary',
                              ]"
                            >
                              {{ moduleInPlan(child.key) ? "Included by plan" : "Not in plan" }}
                            </span>
                            <span
                              v-if="hasModuleOverride(child.key)"
                              :class="[
                                'badge',
                                moduleEnabled(child.key) ? 'badge-soft-primary' : 'badge-soft-warning',
                              ]"
                            >
                              {{ moduleEnabled(child.key) ? "Force enabled" : "Force disabled" }}
                            </span>
                            <span v-else class="badge badge-soft-light text-dark">
                              Using plan default
                            </span>
                          </div>
                        </div>

                        <div class="form-check form-switch mb-0">
                          <input
                            :id="`module-override-${child.key}`"
                            class="form-check-input"
                            type="checkbox"
                            :checked="moduleEnabled(child.key)"
                            @change="setModuleEnabled(child.key, $event.target.checked)"
                          />
                          <label
                            class="form-check-label small text-dark"
                            :for="`module-override-${child.key}`"
                          >
                            {{ moduleEnabled(child.key) ? "Enabled" : "Disabled" }}
                          </label>
                        </div>
                      </div>
                    </div>

                    <div class="d-flex align-items-center justify-content-end gap-2">
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        :disabled="savingModuleOverrides"
                        @click="resetModuleOverridesToPlan"
                      >
                        Reset To Plan
                      </button>
                      <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        :disabled="savingModuleOverrides"
                        @click="saveModuleOverrides"
                      >
                        <span
                          v-if="savingModuleOverrides"
                          class="spinner-border spinner-border-sm me-2"
                        ></span>
                        Save Module Access
                      </button>
                    </div>
                  </div>
                </div>

                <div v-if="isSuperAdmin && isAnnualLicense && activeSubscriptionId" class="p-3 border-bottom">
                  <div class="d-flex align-items-center justify-content-between mb-3">
                    <h5 class="mb-0">Annual License Controls</h5>
                    <span class="badge badge-soft-warning">
                      {{ activeSubStatus || "active" }}
                    </span>
                  </div>

                  <div class="small text-muted mb-2">
                    License expiry: {{ formatDate(licenseExpiresAt) }}
                  </div>
                  <div class="small text-muted mb-3">
                    Grace period end: {{ formatDate(gracePeriodEndsAt) }}
                  </div>

                  <div class="row g-3 mb-3">
                    <div class="col-md-6">
                      <label class="form-label">License Expiry Date</label>
                      <input
                        v-model="annualLicenseForm.license_expires_at"
                        type="date"
                        class="form-control"
                      />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Grace Period End Date</label>
                      <input
                        v-model="annualLicenseForm.grace_period_ends_at"
                        type="date"
                        class="form-control"
                      />
                    </div>
                  </div>

                  <div class="d-flex flex-wrap gap-2 mb-3">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      :disabled="managingLicense"
                      @click="saveAnnualLicenseDates"
                    >
                      Save Dates
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      :disabled="managingLicense"
                      @click="resetAnnualLicenseDates"
                    >
                      Reset Dates
                    </button>
                  </div>

                  <div class="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-success"
                      :disabled="managingLicense"
                      @click="updateAnnualLicense('reactivate')"
                    >
                      Reactivate / Renew
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-warning"
                      :disabled="managingLicense"
                      @click="updateAnnualLicense('suspend')"
                    >
                      Suspend License
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      :disabled="managingLicense"
                      @click="updateAnnualLicense('lock')"
                    >
                      Lock License
                    </button>
                  </div>
                </div>

              <div class="p-3">
                <div class="d-flex align-items-center justify-content-between mb-2">
                  <h6>Social Links</h6>
                </div>

                <div v-if="socialLinks.length" class="d-flex align-items-center mb-3 flex-wrap gap-2">
                  <a
                    v-for="item in socialLinks"
                    :key="item.label"
                    :href="item.url"
                    target="_blank"
                    rel="noopener"
                    class="btn btn-icon btn-sm border rounded-circle"
                    :title="item.label"
                  >
                    <img :src="item.icon" :alt="item.label" class="img-fluid" />
                  </a>
                </div>
                <p v-else class="text-muted mb-0">No social links added.</p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!isTenantWorkspace" class="col-xl-8">
          <div class="bg-white rounded">
            <ul class="nav nav-tabs nav-tabs-bottom nav-justified flex-wrap mb-3">
              <li class="nav-item">
                <button
                  type="button"
                  :class="tabClass('activities')"
                  @click="switchTab('activities')"
                >
                  <i class="ti ti-activity me-1"></i>
                  Activities
                </button>
              </li>
              <li class="nav-item">
                <button
                  type="button"
                  :class="tabClass('notes')"
                  @click="switchTab('notes')"
                >
                  <i class="ti ti-file-description me-1"></i>
                  Notes
                </button>
              </li>
              <li class="nav-item">
                <button
                  type="button"
                  :class="tabClass('calls')"
                  @click="switchTab('calls')"
                >
                  <i class="ti ti-phone-call me-1"></i>
                  Calls
                </button>
              </li>
              <li class="nav-item">
                <button
                  type="button"
                  :class="tabClass('files')"
                  @click="switchTab('files')"
                >
                  <i class="ti ti-files me-1"></i>
                  Files
                </button>
              </li>
              <li class="nav-item">
                <button
                  type="button"
                  :class="tabClass('email')"
                  @click="switchTab('email')"
                >
                  <i class="ti ti-mail-check me-1"></i>
                  Email
                </button>
              </li>
            </ul>
          </div>

          <div v-if="activeTab === 'activities'" class="card border-0">
            <div class="card-header d-flex align-items-center justify-content-between">
              <h5 class="mb-0">Activities</h5>
              <button
                type="button"
                class="btn btn-sm btn-white"
                :disabled="engagementLoading.activities"
                @click="loadActivities"
              >
                Refresh
              </button>
            </div>
            <div class="card-body">
              <EngagementTabSkeleton
                v-if="engagementLoading.activities"
                variant="timeline"
                :count="4"
              />
              <div
                v-else-if="!activities.length"
                class="border rounded p-3 text-muted"
              >
                No activity has been recorded for this company yet.
              </div>
              <div v-else class="d-flex flex-column gap-3">
                <div
                  v-for="item in activities"
                  :key="item.id"
                  class="border rounded p-3"
                >
                  <div class="d-flex align-items-start">
                    <span class="avatar avatar-md avatar-rounded flex-shrink-0 bg-skyblue me-2">
                      <i :class="`${activityIcon(item.type)} fs-20`"></i>
                    </span>
                    <div class="flex-fill">
                      <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <h6 class="fw-medium mb-1">{{ item.title }}</h6>
                        <span class="text-muted small">{{ formatDate(item.occurred_at) }}</span>
                      </div>
                      <p class="mb-1 text-dark">{{ safeText(item.description, "—") }}</p>
                      <div class="small text-muted">
                        <span>{{ userDisplayName(item.user) }}</span>
                        <span v-if="item.meta?.status"> • {{ item.meta.status }}</span>
                        <span v-if="item.meta?.outcome"> • Outcome: {{ item.meta.outcome }}</span>
                        <span v-if="item.meta?.duration_minutes">
                          • {{ item.meta.duration_minutes }} min
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'notes'" class="card border-0">
            <div class="card-header d-flex align-items-center justify-content-between">
              <h5 class="mb-0">Notes</h5>
              <button
                type="button"
                class="btn btn-sm btn-white"
                :disabled="engagementLoading.notes"
                @click="loadNotes"
              >
                Refresh
              </button>
            </div>
            <div class="card-body">
              <div class="border rounded p-3">
                <h6 class="fw-medium mb-2">{{ editingNoteId ? "Edit note" : "Add note" }}</h6>
                <div class="mb-3">
                  <textarea
                    v-model="noteForm.body"
                    class="form-control"
                    rows="4"
                    placeholder="Add an internal note about this company"
                  ></textarea>
                </div>
                <div class="d-flex align-items-center justify-content-end gap-2">
                  <button
                    v-if="editingNoteId"
                    type="button"
                    class="btn btn-outline-light border"
                    @click="resetNoteForm"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary"
                    :disabled="submitting.note"
                    @click="submitNote"
                  >
                    <span
                      v-if="submitting.note"
                      class="spinner-border spinner-border-sm me-2"
                    ></span>
                    {{ editingNoteId ? "Update Note" : "Save Note" }}
                  </button>
                </div>
              </div>

              <div class="mt-3">
                <EngagementTabSkeleton
                  v-if="engagementLoading.notes"
                  variant="form-list"
                  :count="3"
                />
                <div v-else-if="!notes.length" class="border rounded p-3 text-muted">
                  No notes have been added yet.
                </div>
                <div v-else class="d-flex flex-column gap-3">
                  <div v-for="note in notes" :key="note.id" class="border rounded p-3">
                    <div class="d-flex align-items-start justify-content-between gap-3">
                      <div class="flex-fill">
                        <p class="mb-2 text-dark">{{ note.body }}</p>
                        <div class="small text-muted">
                          {{ userDisplayName(note.user) }} • {{ formatDate(note.created_at) }}
                        </div>
                      </div>
                      <div class="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-primary"
                          @click="startEditNote(note)"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          :disabled="submitting.noteDeleteId === note.id"
                          @click="deleteNote(note)"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'calls'" class="card border-0">
            <div class="card-header d-flex align-items-center justify-content-between">
              <h5 class="mb-0">Calls</h5>
              <button
                type="button"
                class="btn btn-sm btn-white"
                :disabled="engagementLoading.calls"
                @click="loadCalls"
              >
                Refresh
              </button>
            </div>
            <div class="card-body">
              <div class="border rounded p-3">
                <h6 class="fw-medium mb-2">
                  {{ editingCallId ? "Edit call log" : "Log a call" }}
                </h6>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Subject</label>
                    <input
                      v-model="callForm.subject"
                      type="text"
                      class="form-control"
                      placeholder="Quarterly review call"
                    />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Call Date & Time</label>
                    <input
                      v-model="callForm.call_datetime"
                      type="datetime-local"
                      class="form-control"
                    />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Duration (minutes)</label>
                    <input
                      v-model="callForm.duration_minutes"
                      type="number"
                      min="0"
                      class="form-control"
                    />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Outcome</label>
                    <input
                      v-model="callForm.outcome"
                      type="text"
                      class="form-control"
                      placeholder="Interested, follow-up required"
                    />
                  </div>
                  <div class="col-12 mb-3">
                    <label class="form-label">Notes</label>
                    <textarea
                      v-model="callForm.notes"
                      class="form-control"
                      rows="4"
                      placeholder="What was discussed during the call?"
                    ></textarea>
                  </div>
                </div>
                <div class="d-flex align-items-center justify-content-end gap-2">
                  <button
                    v-if="editingCallId"
                    type="button"
                    class="btn btn-outline-light border"
                    @click="resetCallForm"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary"
                    :disabled="submitting.call"
                    @click="submitCall"
                  >
                    <span
                      v-if="submitting.call"
                      class="spinner-border spinner-border-sm me-2"
                    ></span>
                    {{ editingCallId ? "Update Call" : "Save Call" }}
                  </button>
                </div>
              </div>

              <div class="mt-3">
                <EngagementTabSkeleton
                  v-if="engagementLoading.calls"
                  variant="form-list"
                  :count="3"
                />
                <div v-else-if="!calls.length" class="border rounded p-3 text-muted">
                  No call logs have been recorded yet.
                </div>
                <div v-else class="d-flex flex-column gap-3">
                  <div v-for="call in calls" :key="call.id" class="border rounded p-3">
                    <div class="d-flex align-items-start justify-content-between gap-3">
                      <div class="flex-fill">
                        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
                          <h6 class="fw-medium mb-1">{{ call.subject }}</h6>
                          <span class="text-muted small">
                            {{ formatDate(call.call_datetime) }}
                          </span>
                        </div>
                        <p class="mb-2 text-dark">{{ safeText(call.notes, "No notes added.") }}</p>
                        <div class="small text-muted">
                          {{ userDisplayName(call.user) }}
                          <span v-if="call.duration_minutes">
                            • {{ call.duration_minutes }} min
                          </span>
                          <span v-if="call.outcome"> • {{ call.outcome }}</span>
                        </div>
                      </div>
                      <div class="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-primary"
                          @click="startEditCall(call)"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          :disabled="submitting.callDeleteId === call.id"
                          @click="deleteCall(call)"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'files'" class="card border-0">
            <div class="card-header d-flex align-items-center justify-content-between">
              <h5 class="mb-0">Files</h5>
              <button
                type="button"
                class="btn btn-sm btn-white"
                :disabled="engagementLoading.files"
                @click="loadFiles"
              >
                Refresh
              </button>
            </div>
            <div class="card-body">
              <div class="border rounded p-3">
                <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div>
                    <h6 class="fw-medium mb-1">Upload a file</h6>
                    <p class="mb-0 text-muted">
                      Attach documents relevant to this company. Maximum file size is 10 MB.
                    </p>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <input
                      ref="fileInput"
                      type="file"
                      class="d-none"
                      @change="handleFileSelection"
                    />
                    <button
                      type="button"
                      class="btn btn-outline-primary"
                      @click="openFilePicker"
                    >
                      Choose File
                    </button>
                    <button
                      type="button"
                      class="btn btn-primary"
                      :disabled="!selectedFile || submitting.file"
                      @click="uploadSelectedFile"
                    >
                      <span
                        v-if="submitting.file"
                        class="spinner-border spinner-border-sm me-2"
                      ></span>
                      Upload
                    </button>
                  </div>
                </div>
                <div v-if="selectedFile" class="small text-muted mt-2">
                  Selected: {{ selectedFile.name }}
                </div>
              </div>

              <div class="mt-3">
                <EngagementTabSkeleton
                  v-if="engagementLoading.files"
                  variant="table"
                  :count="4"
                />
                <div v-else-if="!files.length" class="border rounded p-3 text-muted">
                  No files have been uploaded yet.
                </div>
                <div v-else class="d-flex flex-column gap-3">
                  <div v-for="file in files" :key="file.id" class="border rounded p-3">
                    <div class="d-flex align-items-start justify-content-between gap-3">
                      <div class="flex-fill">
                        <h6 class="fw-medium mb-1">{{ file.file_name }}</h6>
                        <div class="small text-muted">
                          {{ userDisplayName(file.user) }} •
                          {{ formatDate(file.created_at) }} •
                          {{ formatFileSize(file.size) }}
                        </div>
                        <div class="small text-muted mt-1">
                          {{ safeText(file.mime_type, "Unknown type") }}
                        </div>
                      </div>
                      <div class="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-primary"
                          :disabled="submitting.fileDownloadId === file.id"
                          @click="downloadFile(file)"
                        >
                          Download
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          :disabled="submitting.fileDeleteId === file.id"
                          @click="deleteFile(file)"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="card border-0">
            <div class="card-header d-flex align-items-center justify-content-between">
              <h5 class="mb-0">Email</h5>
              <button
                type="button"
                class="btn btn-sm btn-white"
                :disabled="engagementLoading.emails"
                @click="loadEmails"
              >
                Refresh
              </button>
            </div>
            <div class="card-body">
              <div class="border rounded p-3">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">To</label>
                    <input
                      v-model="emailForm.to_email"
                      type="email"
                      class="form-control"
                      :placeholder="companyEmail || 'client@example.com'"
                    />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Subject</label>
                    <input
                      v-model="emailForm.subject"
                      type="text"
                      class="form-control"
                      placeholder="Follow up on your subscription"
                    />
                  </div>
                  <div class="col-12 mb-3">
                    <label class="form-label">Message</label>
                    <textarea
                      v-model="emailForm.body"
                      class="form-control"
                      rows="6"
                      placeholder="Write your message here"
                    ></textarea>
                  </div>
                </div>
                <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div class="small text-muted">
                    Default recipient: {{ safeText(companyEmail, "—") }}
                  </div>
                  <button
                    type="button"
                    class="btn btn-primary"
                    :disabled="submitting.email"
                    @click="submitEmail"
                  >
                    <span
                      v-if="submitting.email"
                      class="spinner-border spinner-border-sm me-2"
                    ></span>
                    Send Email
                  </button>
                </div>
              </div>

              <div class="mt-3">
                <EngagementTabSkeleton
                  v-if="engagementLoading.emails"
                  variant="form-list"
                  :count="3"
                />
                <div v-else-if="!emails.length" class="border rounded p-3 text-muted">
                  No emails have been logged yet.
                </div>
                <div v-else class="d-flex flex-column gap-3">
                  <div v-for="email in emails" :key="email.id" class="border rounded p-3">
                    <div class="d-flex align-items-start justify-content-between gap-3">
                      <div class="flex-fill">
                        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
                          <h6 class="fw-medium mb-1">{{ email.subject }}</h6>
                          <span
                            :class="[
                              'badge',
                              email.status === 'sent'
                                ? 'badge-soft-success'
                                : 'badge-soft-danger',
                            ]"
                          >
                            {{ email.status }}
                          </span>
                        </div>
                        <div class="small text-muted mb-2">
                          To: {{ email.to_email }} •
                          {{ formatDate(email.sent_at || email.created_at) }}
                        </div>
                        <p class="mb-2 text-dark">{{ email.body }}</p>
                        <div class="small text-muted">
                          {{ userDisplayName(email.user) }}
                          <span v-if="email.error_message">
                            • {{ email.error_message }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014-2025 © SmartHR.</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useCompanyStore } from "@/stores/companyStore";
import { usePlanStore } from "@/stores/planStore";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { useAuthStore } from "@/stores/authStore";
import companyEngagementService from "@/services/company-engagement.service";
import tenantService from "@/services/super-admin/tenantService";
import CompanyDetailsSkeleton from "@/components/ui/CompanyDetailsSkeleton.vue";
import EngagementTabSkeleton from "@/components/ui/EngagementTabSkeleton.vue";
import {
  tenantDomainLabel as resolveTenantDomainLabel,
  isTenantHost,
  isTenancyFrontendEnabled,
} from "@/utils/hostname";
import { toast } from "@/utils/toast";

import social01 from "@/assets/img/social/social-01.svg";
import social06 from "@/assets/img/social/social-06.svg";
import social02 from "@/assets/img/social/social-02.svg";
import social03 from "@/assets/img/social/social-03.svg";
import social04 from "@/assets/img/social/social-04.svg";
import social05 from "@/assets/img/social/social-05.svg";

const route = useRoute();
const store = useCompanyStore();
const planStore = usePlanStore();
const subscriptionStore = useSubscriptionStore();
const authStore = useAuthStore();

const updatingStatus = ref(false);
const changingPlan = ref(false);
const managingLicense = ref(false);
const moduleOverrideLoading = ref(false);
const savingModuleOverrides = ref(false);
const annualLicenseForm = ref({
  license_expires_at: "",
  grace_period_ends_at: "",
});
const activeTab = ref("activities");
const fileInput = ref(null);
const selectedFile = ref(null);
const activities = ref([]);
const notes = ref([]);
const calls = ref([]);
const files = ref([]);
const emails = ref([]);
const moduleGroups = ref([]);
const baseModules = ref([]);
const effectiveModules = ref([]);

const workspaceInfo = ref(null);
const workspaceLoading = ref(false);
const workspaceSlug = ref("");
const slugAvailable = ref(false);
const slugCheckMessage = ref("");
const slugPreviewUrl = ref("");
const migratingWorkspace = ref(false);
const runningTenantMigrate = ref(false);
let slugCheckTimer = null;

const tenantDomainLabel = resolveTenantDomainLabel();
const migrationCenterLink = computed(() => {
  const companyId = viewedCompanyId.value;
  return companyId
    ? `/super-admin/migration-center?company_id=${companyId}`
    : "/super-admin/migration-center";
});

const engagementLoading = ref({
  activities: false,
  notes: false,
  calls: false,
  files: false,
  emails: false,
});

const loadedTabs = ref(new Set());

const submitting = ref({
  note: false,
  noteDeleteId: null,
  call: false,
  callDeleteId: null,
  file: false,
  fileDeleteId: null,
  fileDownloadId: null,
  email: false,
});

const editingNoteId = ref(null);
const editingCallId = ref(null);

const noteForm = ref({
  body: "",
});

const callForm = ref({
  subject: "",
  notes: "",
  call_datetime: "",
  duration_minutes: 0,
  outcome: "",
});

const emailForm = ref({
  to_email: "",
  subject: "",
  body: "",
});

const planChangeForm = ref({
  plan_id: "",
  starts_at: new Date().toISOString().slice(0, 10),
  payment_status: "paid",
  payment_method: "Manual",
});

const idParam = computed(() => route.params.id || route.query.id || null);
const viewedCompanyId = computed(() => {
  const id = Number(idParam.value);
  return id && !Number.isNaN(id) ? id : null;
});
const isSuperAdmin = computed(() => authStore.isSuperAdmin);
const isTenantWorkspace = computed(
  () => isTenancyFrontendEnabled() && isTenantHost() && !isSuperAdmin.value
);
const backLink = computed(() =>
  isSuperAdmin.value ? "/super-admin/companies" : "/dashboard/admin-dashboard"
);
const backLabel = computed(() =>
  isSuperAdmin.value ? "Companies" : "Dashboard"
);

const loading = computed(() => store.loading);
const error = computed(() => store.error);
const company = computed(() => store.company || null);
const pageLoading = computed(() => loading.value && !company.value?.id);

const companyName = computed(() => company.value?.company_name || "Company Details");
const companyAddress = computed(() => company.value?.address || null);
const companyPhone = computed(() => company.value?.phone || company.value?.phone_two || null);
const companyEmail = computed(() => company.value?.email || null);
const companyCurrency = computed(
  () => company.value?.currency || company.value?.currency_code || null
);
const companyCountry = computed(() => company.value?.country || null);
const companyCountryCode = computed(() => company.value?.country_code || null);
const companyStatus = computed(() => company.value?.status || null);
const companyCreatedAt = computed(() => company.value?.created_at || null);
const companyUpdatedAt = computed(() => company.value?.updated_at || null);
const logoSrc = computed(() => company.value?.logo_url || null);

const activePlanName = computed(
  () => company.value?.active_subscription?.plan_name || null
);
const activeSubStatus = computed(
  () => company.value?.active_subscription?.status || null
);
const activeSubscriptionId = computed(
  () => company.value?.active_subscription?.id || null
);
const billingPeriod = computed(
  () => company.value?.active_subscription?.billing_period || null
);
const isAnnualLicense = computed(() => billingPeriod.value === "annual_license");
const billingPeriodLabel = computed(() =>
  billingPeriod.value === "annual_license" ? "Annual License" : billingPeriod.value
);
const licenseExpiresAt = computed(
  () => company.value?.active_subscription?.license_expires_at || null
);
const gracePeriodEndsAt = computed(
  () => company.value?.active_subscription?.grace_period_ends_at || null
);
const activePlans = computed(() =>
  (planStore.plans || []).filter(
    (plan) => String(plan.status || "").toLowerCase() === "active"
  )
);
const baseModuleSet = computed(() => new Set(baseModules.value || []));
const effectiveModuleSet = computed(() => new Set(effectiveModules.value || []));

const socialLinks = computed(() => {
  const c = company.value || {};
  return [
    { label: "Facebook", url: c.facebook_profile, icon: social01 },
    { label: "Twitter", url: c.twitter_profile, icon: social06 },
    { label: "LinkedIn", url: c.linkedin_profile, icon: social02 },
    { label: "Skype", url: c.skype_profile, icon: social03 },
    { label: "WhatsApp", url: c.whatsapp_profile, icon: social04 },
    { label: "Instagram", url: c.instagram_profile, icon: social05 },
  ].filter((item) => !!item.url);
});

function tabClass(tab) {
  return [
    "nav-link fw-medium d-flex align-items-center justify-content-center",
    activeTab.value === tab ? "active" : "",
  ];
}

function switchTab(tab) {
  activeTab.value = tab;
  ensureTabLoaded(tab);
}

function resetEngagementCache() {
  loadedTabs.value = new Set();
  activities.value = [];
  notes.value = [];
  calls.value = [];
  files.value = [];
  emails.value = [];
}

async function ensureTabLoaded(tab) {
  if (isTenantWorkspace.value || !company.value?.id || loadedTabs.value.has(tab)) {
    return;
  }

  switch (tab) {
    case "activities":
      await loadActivities();
      break;
    case "notes":
      await loadNotes();
      break;
    case "calls":
      await loadCalls();
      break;
    case "files":
      await loadFiles();
      break;
    case "email":
      await loadEmails();
      break;
    default:
      break;
  }
}

function toggleHeader() {
  const el = document.getElementById("collapse-header");
  if (el) el.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function safeText(val, fallback = "—") {
  if (val === null || val === undefined) return fallback;
  const s = String(val).trim();
  return s.length ? s : fallback;
}

function formatDate(isoString) {
  if (!isoString) return "—";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateInput(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (value) => String(value).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function formatFileSize(bytes) {
  const size = Number(bytes || 0);
  if (!size) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    units.length - 1
  );
  return `${(size / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function userDisplayName(user) {
  return user?.name || user?.email || "Unknown user";
}

function activityIcon(type) {
  return (
    {
      note: "ti ti-notes",
      call: "ti ti-phone-call",
      file: "ti ti-file-text",
      email: "ti ti-mail",
    }[type] || "ti ti-activity"
  );
}

function moduleInPlan(moduleKey) {
  return baseModuleSet.value.has(moduleKey);
}

function moduleEnabled(moduleKey) {
  return effectiveModuleSet.value.has(moduleKey);
}

function hasModuleOverride(moduleKey) {
  return moduleEnabled(moduleKey) !== moduleInPlan(moduleKey);
}

function setModuleEnabled(moduleKey, enabled) {
  const next = new Set(effectiveModules.value || []);
  if (enabled) next.add(moduleKey);
  else next.delete(moduleKey);
  effectiveModules.value = Array.from(next);
}

function groupEnabledCount(group) {
  return (group.children || []).filter((child) => moduleEnabled(child.key)).length;
}

async function resetModuleOverridesToPlan() {
  if (!isSuperAdmin.value || !company.value?.id) return;

  effectiveModules.value = [...baseModules.value];
  savingModuleOverrides.value = true;
  try {
    const res = await store.updateCompanyModuleOverrides(
      company.value.id,
      { overrides: [] },
      viewedCompanyId.value || company.value.id
    );
    baseModules.value = Array.isArray(res?.base_modules) ? res.base_modules : [];
    effectiveModules.value = Array.isArray(res?.effective_modules)
      ? res.effective_modules
      : [];
    toast("Company module access reset to the plan defaults.");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to reset company module access.", "error");
  } finally {
    savingModuleOverrides.value = false;
  }
}

function resetNoteForm() {
  editingNoteId.value = null;
  noteForm.value.body = "";
}

function startEditNote(note) {
  editingNoteId.value = note.id;
  noteForm.value.body = note.body || "";
  switchTab("notes");
}

function resetCallForm() {
  editingCallId.value = null;
  callForm.value = {
    subject: "",
    notes: "",
    call_datetime: "",
    duration_minutes: 0,
    outcome: "",
  };
}

function startEditCall(call) {
  editingCallId.value = call.id;
  callForm.value = {
    subject: call.subject || "",
    notes: call.notes || "",
    call_datetime: formatDateInput(call.call_datetime),
    duration_minutes: Number(call.duration_minutes || 0),
    outcome: call.outcome || "",
  };
  switchTab("calls");
}

function openFilePicker() {
  fileInput.value?.click();
}

function handleFileSelection(event) {
  selectedFile.value = event.target.files?.[0] || null;
}

function clearSelectedFile() {
  selectedFile.value = null;
  if (fileInput.value) fileInput.value.value = "";
}

async function loadActivities() {
  if (isTenantWorkspace.value || !company.value?.id) return;
  engagementLoading.value.activities = true;
  try {
    const res = await companyEngagementService.getActivities(company.value.id);
    activities.value = Array.isArray(res.data) ? res.data : [];
    loadedTabs.value.add("activities");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to load activities.", "error");
  } finally {
    engagementLoading.value.activities = false;
  }
}

async function loadNotes() {
  if (isTenantWorkspace.value || !company.value?.id) return;
  engagementLoading.value.notes = true;
  try {
    const res = await companyEngagementService.getNotes(company.value.id);
    notes.value = Array.isArray(res.data) ? res.data : [];
    loadedTabs.value.add("notes");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to load notes.", "error");
  } finally {
    engagementLoading.value.notes = false;
  }
}

async function loadCalls() {
  if (isTenantWorkspace.value || !company.value?.id) return;
  engagementLoading.value.calls = true;
  try {
    const res = await companyEngagementService.getCalls(company.value.id);
    calls.value = Array.isArray(res.data) ? res.data : [];
    loadedTabs.value.add("calls");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to load calls.", "error");
  } finally {
    engagementLoading.value.calls = false;
  }
}

async function loadFiles() {
  if (isTenantWorkspace.value || !company.value?.id) return;
  engagementLoading.value.files = true;
  try {
    const res = await companyEngagementService.getFiles(company.value.id);
    files.value = Array.isArray(res.data) ? res.data : [];
    loadedTabs.value.add("files");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to load files.", "error");
  } finally {
    engagementLoading.value.files = false;
  }
}

async function loadEmails() {
  if (isTenantWorkspace.value || !company.value?.id) return;
  engagementLoading.value.emails = true;
  try {
    const res = await companyEngagementService.getEmails(company.value.id);
    emails.value = Array.isArray(res.data) ? res.data : [];
    loadedTabs.value.add("email");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to load emails.", "error");
  } finally {
    engagementLoading.value.emails = false;
  }
}

async function loadCompanyById(id) {
  if (isTenantWorkspace.value) {
    await loadWorkspaceCompany();
    return;
  }

  const normalized = Number(id);
  if (!normalized || Number.isNaN(normalized)) return;

  resetEngagementCache();
  await store.fetchCompany(normalized, normalized);
  await loadModuleOverrides(normalized);
  await loadCompanyWorkspace(normalized);
  await ensureTabLoaded(activeTab.value);
}

async function loadWorkspaceCompany() {
  resetEngagementCache();
  await store.fetchCurrentCompany();
}

async function submitNote() {
  if (!company.value?.id || !String(noteForm.value.body || "").trim()) {
    toast("Enter a note before saving.", "error");
    return;
  }

  submitting.value.note = true;
  try {
    if (editingNoteId.value) {
      await companyEngagementService.updateNote(company.value.id, editingNoteId.value, {
        body: noteForm.value.body.trim(),
      });
      toast("Note updated successfully.");
    } else {
      await companyEngagementService.createNote(company.value.id, {
        body: noteForm.value.body.trim(),
      });
      toast("Note saved successfully.");
    }

    resetNoteForm();
    await Promise.all([loadNotes(), loadActivities()]);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to save note.", "error");
  } finally {
    submitting.value.note = false;
  }
}

async function deleteNote(note) {
  if (!company.value?.id) return;
  submitting.value.noteDeleteId = note.id;
  try {
    await companyEngagementService.deleteNote(company.value.id, note.id);
    if (editingNoteId.value === note.id) resetNoteForm();
    toast("Note deleted.");
    await Promise.all([loadNotes(), loadActivities()]);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to delete note.", "error");
  } finally {
    submitting.value.noteDeleteId = null;
  }
}

async function submitCall() {
  if (!company.value?.id || !String(callForm.value.subject || "").trim()) {
    toast("Enter a call subject before saving.", "error");
    return;
  }
  if (!callForm.value.call_datetime) {
    toast("Choose the call date and time.", "error");
    return;
  }

  const payload = {
    subject: callForm.value.subject.trim(),
    notes: String(callForm.value.notes || "").trim() || null,
    call_datetime: callForm.value.call_datetime,
    duration_minutes: Number(callForm.value.duration_minutes || 0),
    outcome: String(callForm.value.outcome || "").trim() || null,
  };

  submitting.value.call = true;
  try {
    if (editingCallId.value) {
      await companyEngagementService.updateCall(company.value.id, editingCallId.value, payload);
      toast("Call updated successfully.");
    } else {
      await companyEngagementService.createCall(company.value.id, payload);
      toast("Call logged successfully.");
    }

    resetCallForm();
    await Promise.all([loadCalls(), loadActivities()]);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to save call.", "error");
  } finally {
    submitting.value.call = false;
  }
}

async function deleteCall(call) {
  if (!company.value?.id) return;
  submitting.value.callDeleteId = call.id;
  try {
    await companyEngagementService.deleteCall(company.value.id, call.id);
    if (editingCallId.value === call.id) resetCallForm();
    toast("Call deleted.");
    await Promise.all([loadCalls(), loadActivities()]);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to delete call.", "error");
  } finally {
    submitting.value.callDeleteId = null;
  }
}

async function uploadSelectedFile() {
  if (!company.value?.id || !selectedFile.value) return;

  submitting.value.file = true;
  try {
    await companyEngagementService.uploadFile(company.value.id, selectedFile.value);
    clearSelectedFile();
    toast("File uploaded successfully.");
    await Promise.all([loadFiles(), loadActivities()]);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to upload file.", "error");
  } finally {
    submitting.value.file = false;
  }
}

async function downloadFile(file) {
  if (!company.value?.id) return;

  submitting.value.fileDownloadId = file.id;
  try {
    const res = await companyEngagementService.downloadFile(company.value.id, file.id);
    const blob = new Blob([res.data], {
      type: file.mime_type || "application/octet-stream",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.file_name || "company-file";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to download file.", "error");
  } finally {
    submitting.value.fileDownloadId = null;
  }
}

async function deleteFile(file) {
  if (!company.value?.id) return;

  submitting.value.fileDeleteId = file.id;
  try {
    await companyEngagementService.deleteFile(company.value.id, file.id);
    toast("File deleted.");
    await Promise.all([loadFiles(), loadActivities()]);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to delete file.", "error");
  } finally {
    submitting.value.fileDeleteId = null;
  }
}

async function submitEmail() {
  if (!company.value?.id) return;
  if (!String(emailForm.value.subject || "").trim()) {
    toast("Enter an email subject.", "error");
    return;
  }
  if (!String(emailForm.value.body || "").trim()) {
    toast("Enter an email message.", "error");
    return;
  }

  submitting.value.email = true;
  try {
    await companyEngagementService.sendEmail(company.value.id, {
      to_email: String(emailForm.value.to_email || "").trim() || undefined,
      subject: emailForm.value.subject.trim(),
      body: emailForm.value.body.trim(),
    });

    emailForm.value = {
      to_email: companyEmail.value || "",
      subject: "",
      body: "",
    };
    toast("Email sent successfully.");
    await Promise.all([loadEmails(), loadActivities()]);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to send email.", "error");
    await Promise.all([loadEmails(), loadActivities()]);
  } finally {
    submitting.value.email = false;
  }
}

async function changeCompanyStatus(status) {
  if (!isSuperAdmin.value || !company.value?.id) return;

  updatingStatus.value = true;
  try {
    await store.updateCompany(
      company.value.id,
      { status },
      true,
      viewedCompanyId.value || company.value.id
    );
    await loadCompanyById(company.value.id);
    toast(`Company marked as ${status}.`);
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to update company status.", "error");
  } finally {
    updatingStatus.value = false;
  }
}

async function activatePaidLicense() {
  if (!isSuperAdmin.value || !activeSubscriptionId.value) return;

  managingLicense.value = true;
  try {
    await subscriptionStore.updateSubscription(activeSubscriptionId.value, {
      reactivate: true,
    });
    await loadCompanyById(company.value.id);
    toast("Trial converted to an active paid license.");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to activate paid license.", "error");
  } finally {
    managingLicense.value = false;
  }
}

async function changeCompanyPlan() {
  if (!isSuperAdmin.value || !company.value?.id || !planChangeForm.value.plan_id) return;

  changingPlan.value = true;
  try {
    await store.changeCompanyPlan(
      company.value.id,
      {
        plan_id: Number(planChangeForm.value.plan_id),
        starts_at: planChangeForm.value.starts_at || undefined,
        payment_status: planChangeForm.value.payment_status || undefined,
        payment_method: planChangeForm.value.payment_method || undefined,
      },
      viewedCompanyId.value || company.value.id
    );
    await loadCompanyById(company.value.id);
    toast("Company plan changed successfully.");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to change company plan.", "error");
  } finally {
    changingPlan.value = false;
  }
}

async function updateAnnualLicense(action) {
  if (!isSuperAdmin.value || !activeSubscriptionId.value) return;

  managingLicense.value = true;
  try {
    if (action === "reactivate") {
      await subscriptionStore.updateSubscription(activeSubscriptionId.value, {
        reactivate: true,
      });
    } else if (action === "lock") {
      await subscriptionStore.updateSubscription(activeSubscriptionId.value, {
        status: "locked",
      });
    } else if (action === "suspend") {
      await subscriptionStore.updateSubscription(activeSubscriptionId.value, {
        status: "suspended",
      });
    }

    await loadCompanyById(company.value.id);
    toast("Annual license updated successfully.");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to update annual license.", "error");
  } finally {
    managingLicense.value = false;
  }
}

async function saveAnnualLicenseDates() {
  if (!isSuperAdmin.value || !activeSubscriptionId.value) return;

  managingLicense.value = true;
  try {
    await subscriptionStore.updateSubscription(activeSubscriptionId.value, {
      license_expires_at: annualLicenseForm.value.license_expires_at || null,
      grace_period_ends_at: annualLicenseForm.value.grace_period_ends_at || null,
    });
    await loadCompanyById(company.value.id);
    toast("Annual license dates updated successfully.");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to update annual license dates.", "error");
  } finally {
    managingLicense.value = false;
  }
}

function resetAnnualLicenseDates() {
  annualLicenseForm.value.license_expires_at = licenseExpiresAt.value
    ? String(licenseExpiresAt.value).slice(0, 10)
    : "";
  annualLicenseForm.value.grace_period_ends_at = gracePeriodEndsAt.value
    ? String(gracePeriodEndsAt.value).slice(0, 10)
    : "";
}

async function loadModuleOverrides(companyId) {
  if (!isSuperAdmin.value || !companyId) return;

  moduleOverrideLoading.value = true;
  try {
    const res = await store.fetchCompanyModuleOverrides(
      companyId,
      viewedCompanyId.value || companyId
    );
    moduleGroups.value = Array.isArray(res?.groups) ? res.groups : [];
    baseModules.value = Array.isArray(res?.base_modules) ? res.base_modules : [];
    effectiveModules.value = Array.isArray(res?.effective_modules)
      ? res.effective_modules
      : [];
  } catch (e) {
    moduleGroups.value = [];
    baseModules.value = [];
    effectiveModules.value = [];
    toast(e?.response?.data?.message || "Failed to load company module access.", "error");
  } finally {
    moduleOverrideLoading.value = false;
  }
}

async function saveModuleOverrides() {
  if (!isSuperAdmin.value || !company.value?.id) return;

  const allKeys = new Set([
    ...baseModules.value,
    ...effectiveModules.value,
    ...moduleGroups.value.flatMap((group) => (group.children || []).map((child) => child.key)),
  ]);

  const overrides = [];
  allKeys.forEach((moduleKey) => {
    const baseEnabled = baseModuleSet.value.has(moduleKey);
    const currentEnabled = effectiveModuleSet.value.has(moduleKey);

    if (baseEnabled !== currentEnabled) {
      overrides.push({
        module_key: moduleKey,
        enabled: currentEnabled,
      });
    }
  });

  savingModuleOverrides.value = true;
  try {
    const res = await store.updateCompanyModuleOverrides(
      company.value.id,
      { overrides },
      viewedCompanyId.value || company.value.id
    );
    baseModules.value = Array.isArray(res?.base_modules) ? res.base_modules : [];
    effectiveModules.value = Array.isArray(res?.effective_modules)
      ? res.effective_modules
      : [];
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to save company module access.", "error");
  } finally {
    savingModuleOverrides.value = false;
  }
}

async function loadCompanyWorkspace(companyId) {
  if (!isSuperAdmin.value || !companyId) return;

  workspaceLoading.value = true;
  try {
    const res = await tenantService.getCompanyWorkspace(companyId);
    workspaceInfo.value = res?.data || null;
    workspaceSlug.value =
      workspaceInfo.value?.tenant?.slug ||
      workspaceInfo.value?.suggested_slug ||
      "";
    slugPreviewUrl.value = workspaceInfo.value?.workspace_url || "";
    if (!workspaceInfo.value?.has_workspace && workspaceSlug.value) {
      await checkWorkspaceSlug();
    }
  } catch (e) {
    workspaceInfo.value = null;
    toast(e?.response?.data?.message || "Failed to load workspace details.", "error");
  } finally {
    workspaceLoading.value = false;
  }
}

async function copyWorkspaceDatabaseName() {
  const name = String(workspaceInfo.value?.database_name || "").trim();
  if (!name) {
    toast("No database name to copy.", "warning");
    return;
  }

  try {
    await navigator.clipboard.writeText(name);
    toast("Database name copied.", "success");
  } catch (_) {
    toast("Could not copy database name.", "error");
  }
}

function queueSlugCheck() {
  if (slugCheckTimer) clearTimeout(slugCheckTimer);
  slugCheckTimer = setTimeout(() => {
    checkWorkspaceSlug();
  }, 350);
}

async function checkWorkspaceSlug() {
  const slug = String(workspaceSlug.value || "").trim();
  if (!slug) {
    slugAvailable.value = false;
    slugCheckMessage.value = "";
    slugPreviewUrl.value = "";
    return;
  }

  try {
    const res = await tenantService.checkSlug(slug, viewedCompanyId.value);
    slugAvailable.value = !!res?.data?.available;
    slugPreviewUrl.value = res?.data?.preview_url || "";
    slugCheckMessage.value = slugAvailable.value
      ? "This workspace URL is available."
      : "This workspace URL is already taken.";
  } catch (e) {
    slugAvailable.value = false;
    slugCheckMessage.value =
      e?.response?.data?.message || "Could not validate workspace slug.";
  }
}

async function provisionWorkspace() {
  if (!company.value?.id || !workspaceSlug.value) return;

  migratingWorkspace.value = true;
  try {
    const res = await tenantService.migrateCompanyToTenant(company.value.id, {
      slug: workspaceSlug.value,
    });
    toast(res?.data?.message || "Workspace provisioned.", "success");
    await loadCompanyWorkspace(company.value.id);
  } catch (e) {
    toast(
      e?.response?.data?.message ||
        e?.response?.data?.report?.errors?.[0] ||
        "Failed to provision workspace.",
      "error"
    );
  } finally {
    migratingWorkspace.value = false;
  }
}

async function runTenantSchemaMigrate() {
  const tenantId = workspaceInfo.value?.tenant?.id;
  if (!tenantId) return;

  runningTenantMigrate.value = true;
  try {
    const res = await tenantService.runTenantMigrations(tenantId);
    toast(res?.data?.message || "Tenant migrations completed.", "success");
  } catch (e) {
    toast(e?.response?.data?.message || "Failed to run tenant migrations.", "error");
  } finally {
    runningTenantMigrate.value = false;
  }
}

onMounted(() => {
  if (isSuperAdmin.value && !planStore.plans?.length) {
    planStore.fetchPlans({ status: "active" });
  }
  if (isTenantWorkspace.value) {
    loadWorkspaceCompany();
    return;
  }
  if (idParam.value) loadCompanyById(idParam.value);
});

watch(
  () => idParam.value,
  (newId, oldId) => {
    if (isTenantWorkspace.value) return;
    if (newId && newId !== oldId) {
      resetNoteForm();
      resetCallForm();
      clearSelectedFile();
      loadCompanyById(newId);
    }
  }
);

  watch(
    () => company.value?.active_subscription,
    (subscription) => {
      if (!subscription) return;

    planChangeForm.value.plan_id = subscription.plan_id
      ? String(subscription.plan_id)
      : "";
    planChangeForm.value.starts_at = subscription.starts_at
      ? String(subscription.starts_at).slice(0, 10)
      : new Date().toISOString().slice(0, 10);
    // SuperAdmin plan changes default to paid/active — don't inherit unpaid trial.
    planChangeForm.value.payment_status = "paid";
    planChangeForm.value.payment_method = subscription.payment_method || "Manual";
      annualLicenseForm.value.license_expires_at = subscription.license_expires_at
        ? String(subscription.license_expires_at).slice(0, 10)
        : "";
      annualLicenseForm.value.grace_period_ends_at = subscription.grace_period_ends_at
        ? String(subscription.grace_period_ends_at).slice(0, 10)
        : "";
    },
    { immediate: true }
  );

watch(
  () => companyEmail.value,
  (email) => {
    if (!emailForm.value.to_email) {
      emailForm.value.to_email = email || "";
    }
  },
  { immediate: true }
);
</script>

